import { passFromState, passPayload } from '@/config/membershipTierWizardPass'
import {
  CUSTOM_INTERVAL,
  defaultInterval,
  defaultWizardData,
  INTERVAL_PRESETS,
  intervalKey,
  selectedIntervals,
  WEEKDAY_SCHEDULE,
  WEEKDAYS_INTERVAL
} from '@/config/membershipTierWizardSteps'

const CENTS_PER_DOLLAR = 100
const AUTOCHARGE_WITHOUT_SETTING = 'never'

const toId = value => (value === null || value === undefined || value === '' ? null : Number(value))

const toKey = value => (value === null || value === undefined ? null : String(value))

const intervalPayload = (interval, isStart) =>
  interval.unit === WEEKDAY_SCHEDULE
    ? { unit: WEEKDAY_SCHEDULE, weekdays: interval.weekdays, default: isStart }
    : { unit: interval.unit, count: interval.count, default: isStart }

const intervalsPayload = (data, editing) => {
  const options = { snap: !editing, keepInvalid: editing }
  const start = defaultInterval(data, options)
  const startKey = start ? intervalKey(start) : null
  const keepsNoStart = editing && startKey !== null && data.ordering.startWithoutDefault === startKey
  return selectedIntervals(data, options).map(interval =>
    intervalPayload(interval, !keepsNoStart && intervalKey(interval) === startKey)
  )
}

const EXPIRY_PAYLOADS = {
  week: { expiry_type: 'week', expiry_value: 1 },
  month: { expiry_type: 'month', expiry_value: 1 },
  never: { expiry_type: null, expiry_value: null }
}

const expiryPayload = ({ expiry, customExpiry }) =>
  expiry === 'custom' && customExpiry
    ? { expiry_type: customExpiry.type, expiry_value: customExpiry.value }
    : EXPIRY_PAYLOADS[expiry] ?? EXPIRY_PAYLOADS.never

const mealPlanPayload = plan => {
  const monthly = plan.grantInterval === 'monthly'
  return {
    price_cents: plan.priceCents,
    billing_interval: plan.billingInterval,
    item_count: plan.itemCount,
    category_id: toId(plan.categoryId),
    grant_interval: plan.grantInterval,
    grant_day: monthly ? null : plan.grantDay,
    grant_date: monthly ? plan.grantDate : null,
    ...expiryPayload(plan)
  }
}

const pricingPayload = (data, editing) => {
  const { pricing } = data
  if (data.includes.memberPrices) {
    return {
      mode: pricing.mode,
      percent: pricing.mode === 'percent' ? Number(pricing.percent) : null,
      amount_cents: pricing.mode === 'amount' ? pricing.amountCents : null
    }
  }
  const mode = editing && !pricing.startedWithMemberPrices ? 'keep' : 'regular'
  return { mode, percent: null, amount_cents: null }
}

const newPackagePayload = pkg => ({
  store_id: toId(pkg.storeId),
  name: pkg.name.trim(),
  description: pkg.description.trim(),
  minimum_items_count: pkg.minimumItems,
  minimum_price_cents: pkg.minimumPriceCents,
  open_to_all: pkg.openToAll
})

const signUpFeePayload = fee => (fee.enabled ? { name: fee.name.trim(), amount_cents: fee.amountCents } : null)

const recurringFeePayload = fee => {
  if (!fee.enabled) return null
  return {
    name: fee.name.trim(),
    amount_cents: fee.amountCents,
    interval_type: fee.intervalType,
    interval_amount: fee.intervalType === 'dollar_amount' ? fee.every * CENTS_PER_DOLLAR : fee.every
  }
}

const subscriptionStoresPayload = ({ subscriptionStoreIds, defaultSubscriptionStoreId }) =>
  subscriptionStoreIds === null
    ? { subscription_store_ids: null, default_subscription_store_id: null }
    : {
        subscription_store_ids: subscriptionStoreIds.map(Number),
        default_subscription_store_id: toId(defaultSubscriptionStoreId)
      }

export const buildSetupPayload = (data, { editing = false } = {}) => {
  const atCheckout = data.joining.mode === 'checkout'
  const repeat = data.includes.repeat
  return {
    name: data.name.trim(),
    description: data.description.trim(),
    joining: data.joining.mode,
    store_ids: atCheckout ? data.joining.storeIds.map(Number) : [],
    is_default: data.joining.isDefault,
    downgrade_tier_id: toId(data.joining.downgradeTierId),
    upgrade_tier_ids: data.joining.upgradeTierIds.map(Number),
    recurring: repeat,
    intervals: repeat ? intervalsPayload(data, editing) : [],
    autocharge: data.ordering.autocharge,
    ...(repeat
      ? subscriptionStoresPayload(data.ordering)
      : { subscription_store_ids: null, default_subscription_store_id: null }),
    minimum_order_count: data.ordering.minimumOrders,
    meal_plan: data.includes.mealPlan ? mealPlanPayload(data.mealPlan) : null,
    pricing: pricingPayload(data, editing),
    package_ids: data.packageIds.map(Number),
    new_packages: data.newPackages.map(newPackagePayload),
    pass: passPayload(data.pass),
    sign_up_fee: signUpFeePayload(data.fees.signUp),
    recurring_fee: recurringFeePayload(data.fees.recurring)
  }
}

const presetKeyOf = interval =>
  interval.unit !== WEEKDAY_SCHEDULE &&
  INTERVAL_PRESETS.some(preset => preset.unit === interval.unit && preset.count === interval.count)
    ? intervalKey(interval)
    : null

const asInterval = interval =>
  interval.unit === WEEKDAY_SCHEDULE
    ? { unit: WEEKDAY_SCHEDULE, weekdays: interval.weekdays }
    : { unit: interval.unit, count: interval.count }

const startFields = start => {
  if (!start) return {}
  if (start.unit === WEEKDAY_SCHEDULE) return { every: WEEKDAYS_INTERVAL, weekdays: start.weekdays }
  const presetKey = presetKeyOf(start)
  return presetKey ? { every: presetKey } : { every: CUSTOM_INTERVAL, custom: { count: start.count, unit: start.unit } }
}

const orderingFromState = (state, defaults) => {
  const intervals = state.intervals ?? []
  const marked = intervals.find(interval => interval.default)
  const start = marked ?? intervals[0]
  const others = intervals.filter(interval => interval !== start)
  return {
    ...defaults,
    startWithoutDefault: !marked && start ? intervalKey(asInterval(start)) : null,
    ...startFields(start),
    canSwitch: others.length > 0,
    alsoOffer: others.map(presetKeyOf).filter(Boolean),
    customExtras: others.filter(interval => !presetKeyOf(interval)).map(asInterval),
    autocharge: state.autocharge ?? AUTOCHARGE_WITHOUT_SETTING,
    allowChoice: state.autocharge === 'choice',
    subscriptionStoreIds: (state.subscription_store_ids ?? []).map(String),
    defaultSubscriptionStoreId: toKey(state.default_subscription_store_id),
    minimumOrders: state.minimum_order_count ?? 1
  }
}

const expiryFromState = plan => {
  if (plan.expiry_type === null && plan.expiry_value === null) return { expiry: 'never', customExpiry: null }
  if (plan.expiry_value === 1 && (plan.expiry_type === 'week' || plan.expiry_type === 'month')) {
    return { expiry: plan.expiry_type, customExpiry: null }
  }
  return { expiry: 'custom', customExpiry: { type: plan.expiry_type, value: plan.expiry_value } }
}

const mealPlanFromState = (plan, defaults) =>
  plan
    ? {
        ...defaults,
        priceCents: plan.price_cents,
        billingInterval: plan.billing_interval,
        itemCount: plan.item_count,
        categoryId: toKey(plan.category_id),
        grantInterval: plan.grant_interval,
        grantDay: plan.grant_day ?? defaults.grantDay,
        grantDate: plan.grant_date ?? defaults.grantDate,
        ...expiryFromState(plan)
      }
    : defaults

const feesFromState = (state, defaults) => ({
  signUp: state.sign_up_fee
    ? { enabled: true, name: state.sign_up_fee.name ?? '', amountCents: state.sign_up_fee.amount_cents }
    : defaults.signUp,
  recurring: state.recurring_fee
    ? {
        enabled: true,
        name: state.recurring_fee.name ?? '',
        amountCents: state.recurring_fee.amount_cents,
        intervalType: state.recurring_fee.interval_type,
        every:
          state.recurring_fee.interval_type === 'dollar_amount'
            ? state.recurring_fee.interval_amount / CENTS_PER_DOLLAR
            : state.recurring_fee.interval_amount
      }
    : defaults.recurring
})

export const draftFromState = (state, meta = {}) => {
  const defaults = defaultWizardData()
  const memberPrices = (meta.custom_price_count ?? 0) > 0
  return {
    ...defaults,
    name: state.name ?? '',
    description: state.description ?? '',
    joining: {
      mode: state.joining,
      storeIds: (state.store_ids ?? []).map(String),
      isDefault: Boolean(state.is_default),
      downgradeTierId: toKey(state.downgrade_tier_id),
      upgradeTierIds: (state.upgrade_tier_ids ?? []).map(String)
    },
    includes: { repeat: Boolean(state.recurring), mealPlan: Boolean(state.meal_plan), memberPrices },
    ordering: orderingFromState(state, defaults.ordering),
    mealPlan: mealPlanFromState(state.meal_plan, defaults.mealPlan),
    pricing: {
      mode: memberPrices ? 'keep' : null,
      percent: null,
      amountCents: null,
      startedWithMemberPrices: memberPrices
    },
    packageIds: (state.package_ids ?? []).map(String),
    newPackages: [],
    pass: passFromState(state.pass),
    fees: feesFromState(state, defaults.fees)
  }
}
